/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Select, Table, message } from "antd";
import ReactJoyride from "react-joyride";
import { Plus, Search } from "lucide-react";

import { DOMAIN_ID, ORG_ID } from "../../constants";
import { useDebounce } from "../../common/debounce";
import { dispatchGetAllAdUsers, dispatchGetAdUserCounts } from "../../utils/apiservice";
import { logOutUser } from "../../common";
import { AN_COLORS } from "../../design-system/tokens";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  SquareBadge,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../design-system/tableUi";

import UserEnrollment from "../../components/enrollment/UserEnrollment";
import { canInviteOthers } from "../../components/enrollment/enrollmentCommon";
import ImportUsers from "../../components/directory/users/ImportUsers";
import AddUser from "../../components/directory/users/AddUser";

const PAGE_SIZE = 10;

// Keyed by the AD user id, falling back to email so blank keys can't collide.
const rowKeyOf = (user) => String(user?.id || user?.emailId || "");

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Pending", label: "Pending" },
];

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "email", label: "Email" },
  { value: "ou", label: "OU" },
  { value: "type", label: "Type" },
  { value: "status", label: "Status" },
];

export default function Users() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const mfa = queryParams.get("mfa");
  const incomingFilter = location?.state?.filter;

  const [notification, notificationContext] = message.useMessage();

  // Modal state
  const [isAddUser, setIsAddUser] = useState(false);
  const [isImportUser, setIsImportUser] = useState(false);

  // Row whose enrollment detail is open. One at a time — each open panel reads
  // its own enrollment status, and only the visible one should be doing that.
  const [enrollmentUserKey, setEnrollmentUserKey] = useState(null);
  // Bumped by the ⋯ menu's "Register device" to open the dialog inside that
  // row's panel. Counter rather than a boolean so re-picking the same user from
  // the menu re-opens it.
  const [registerSignal, setRegisterSignal] = useState({ key: null, n: 0 });

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 600);

  // Status filter
  const [statusFilter, setStatusFilter] = useState(null);

  // Sort (client-side over visible page)
  const [sortBy, setSortBy] = useState("default");

  // List + pagination
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ active: null, pending: null });
  const [isLoading, setIsLoading] = useState(false);
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  // Filter payload sent to the API. directoryName is preserved from
  // location.state so cross-page filter handoffs (Identity Provider → Users)
  // still work without exposing a directory dropdown here.
  const [filterParams, setFilterParams] = useState({
    emailId: "",
    status: "",
    logoname: "",
    directoryName: "",
  });

  // Tour
  const [isOnBoarding, setIsOnboarding] = useState(false);
  const [isTour, setTour] = useState(true);

  // ---- Sync UI controls into filterParams + reset to page 1 ----
  useEffect(() => {
    setFilterParams((prev) => ({ ...prev, emailId: debouncedSearch || "" }));
    setPaginationParams((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    setFilterParams((prev) => ({ ...prev, status: statusFilter || "" }));
    setPaginationParams((p) => ({ ...p, page: 1 }));
  }, [statusFilter]);

  useEffect(() => {
    if (incomingFilter) {
      setFilterParams((prev) => ({ ...prev, ...incomingFilter }));
      setPaginationParams((p) => ({ ...p, page: 1 }));
    }
  }, [incomingFilter]);

  useEffect(() => {
    setIsOnboarding(!!mfa);
  }, [mfa]);

  const sortedUsers = useMemo(() => {
    if (sortBy === "default") return users;
    const arr = [...users];
    const keyMap = {
      email: "emailId",
      ou: "ou",
      type: "userType",
      status: "status",
    };
    const k = keyMap[sortBy];
    arr.sort((a, b) =>
      String(a[k] || "").localeCompare(
        String(b[k] || ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      )
    );
    return arr;
  }, [users, sortBy]);

  // ---- API calls ----
  const fetchAdUser = () => {
    setIsLoading(true);
    const payload = {
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      pageNo: paginationParams.page,
      pageSize: paginationParams.pageSize,
      filters: filterParams,
    };
    dispatchGetAllAdUsers({
      payload,
      onSuccess: (res) => {
        setIsLoading(false);
        setTotalCount(res?.count || 0);
        const usersList = (res?.users || []).map((u) => {
          if (u?.userType?.toLowerCase() === "serviceaccount")
            return { ...u, userType: "Service Account", isEntra: true };
          if (u?.userType?.toLowerCase() === "entrauser")
            return { ...u, userType: "Service Account", isEntra: true };
          if (u?.userType?.toLowerCase() === "normaluser")
            return { ...u, userType: "User", isUser: true };
          return u;
        });
        setUsers(usersList);
      },
      onFailure: ({ message: m, isLogout }) => {
        setIsLoading(false);
        notification.error({ content: m });
        if (isLogout) logOutUser();
      },
      onError: ({ message: m }) => {
        setIsLoading(false);
        notification.error({ content: m });
      },
    });
  };

  // Counts call: same endpoint with a large pageSize and the email filter only
  // (no status filter), so we get an honest Active / Pending breakdown.
  const fetchStatusCounts = () => {
    const payload = {
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      pageNo: 1,
      pageSize: 10000,
      filters: { ...filterParams, status: "" },
    };
    dispatchGetAdUserCounts({
      payload,
      onSuccess: (res) => {
        const list = res?.users || [];
        let active = 0;
        let pending = 0;
        for (const u of list) {
          const s = (u.status || "").toLowerCase();
          if (s === "active") active += 1;
          else if (s === "pending") pending += 1;
        }
        setStatusCounts({ active, pending });
      },
      onFailure: ({ isLogout }) => {
        if (isLogout) logOutUser();
      },
      onError: () => {},
    });
  };

  useEffect(() => {
    fetchAdUser();
  }, [filterParams, paginationParams]);

  useEffect(() => {
    // Stats only depend on the email + directoryName filter, not the status tab.
    fetchStatusCounts();
  }, [filterParams.emailId, filterParams.directoryName]);

  // ---- Filters ----
  const resetPage = () => setPaginationParams((p) => ({ ...p, page: 1 }));

  const clearDirectoryFilter = () => {
    setFilterParams((prev) => ({ ...prev, directoryName: "" }));
    // The filter arrived via location.state; drop it so a remount doesn't
    // reapply it.
    if (incomingFilter) navigate(location.pathname, { replace: true });
    resetPage();
  };

  const appliedFilters = [
    search && {
      key: "email",
      label: `Email · ${search}`,
      clear: () => setSearch(""),
    },
    statusFilter && {
      key: "status",
      label: `Status · ${statusFilter}`,
      clear: () => setStatusFilter(null),
    },
    filterParams.directoryName && {
      key: "provider",
      label: `Provider · ${filterParams.directoryName}`,
      clear: clearDirectoryFilter,
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter(null);
    if (filterParams.directoryName) clearDirectoryFilter();
  };

  // ---- Columns ----
  const columns = [
    {
      title: "Username",
      dataIndex: "emailId",
      key: "emailId",
      render: (_, user) => (
        <div style={{ minWidth: 0 }}>
          <div
            style={{ fontWeight: 500, color: AN_COLORS.ink }}
            title={user.emailId}
          >
            {user.emailId || "—"}
          </div>
          {user.activeDirectoryName && (
            <div style={{ fontSize: 11.5, color: AN_COLORS.muted }}>
              {user.activeDirectoryName}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "OU",
      dataIndex: "ou",
      key: "ou",
      render: (_, user) => (
        <div style={{ minWidth: 0 }}>
          <MonoCell>{user.ou}</MonoCell>
          {user.logoname && (
            <div style={{ fontSize: 11, color: AN_COLORS.muted }}>{user.logoname}</div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "directoryRole",
      key: "directoryRole",
      width: 150,
      render: (text) => (
        <SquareBadge tone={String(text).toLowerCase().includes("service") ? "info" : undefined}>
          {text}
        </SquareBadge>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (text) => <StatusPill status={text} />,
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, user) => {
        // Registering a device mints an invite that enrols a phone as this user,
        // so the entry point is absent for non-admins rather than shown and
        // 403ing.
        const canRegister = canInviteOthers();
        return (
          <RowActionMenu
            disabled={!canRegister}
            items={
              canRegister
                ? [
                    {
                      key: "register",
                      label: "Register device",
                      // Expands the row's enrollment panel and tells it to open
                      // the dialog, so both entry points share one invite
                      // lifecycle.
                      onClick: () => {
                        const key = rowKeyOf(user);
                        setEnrollmentUserKey(key);
                        setRegisterSignal((prev) => ({ key, n: prev.n + 1 }));
                      },
                    },
                  ]
                : []
            }
          />
        );
      },
    },
  ];

  // ---- Add Users dropdown ----
  const addUsersMenu = useMemo(
    () => ({
      items: [
        {
          key: "add",
          label: (
            <Link
              onClick={() => {
                setIsAddUser(true);
                setIsOnboarding(false);
              }}
            >
              Add User
            </Link>
          ),
        },
        {
          key: "import",
          label: (
            <Link
              onClick={() => {
                setIsImportUser(true);
                setIsOnboarding(false);
              }}
            >
              Import Users
            </Link>
          ),
        },
      ],
    }),
    []
  );

  // ---- Tour ----
  const startTour = () => {
    const tourStep = [
      {
        target: "#onboarding_step1",
        disableBeacon: true,
        content: (
          <span>
            <h4 style={{ marginBottom: 0 }}>Add User or Import the Bulk Users</h4>
            <br />
            Add an individual user or add many users in bulk by uploading the CSV file.
          </span>
        ),
        placement: "left",
        hideFooter: true,
      },
    ];
    const handleTourCallback = ({ status }) => {
      if (status === "finished" || status === "skipped") setTour(false);
    };
    return (
      <ReactJoyride
        steps={tourStep}
        run={isTour}
        floaterProps={{ disableAnimation: true }}
        callback={handleTourCallback}
        continuous
        scrollToFirstStep
        disableScrollParentFix
        scrollOffset={100}
        showSkipButton
        showProgress
        hideCloseButton
        styles={{
          buttonNext: { backgroundColor: "blue", color: "#ffffff" },
          buttonBack: { color: "#1677ff" },
        }}
      />
    );
  };

  return (
    <PageShell>
      {notificationContext}
      {isOnBoarding && startTour()}

      <PageHeader
        eyebrow="Admin · Directory · Users"
        title="Users"
        subtitle="Directory and service account identities synced from your identity providers."
        chips={
          <>
            <CountChip count={totalCount} label={totalCount === 1 ? "User" : "Users"} />
            {statusCounts.active != null && (
              <CountChip count={statusCounts.active} label="Active" />
            )}
            {statusCounts.pending != null && (
              <CountChip count={statusCounts.pending} label="Pending" />
            )}
          </>
        }
        actions={
          <Dropdown
            menu={addUsersMenu}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
            open={mfa ? true : undefined}
          >
            <Button id="onboarding_step1" type="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
              Add Users
            </Button>
          </Dropdown>
        }
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={sortedUsers.length}
        total={totalCount}
      >
        <FilterField label="Search email">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="rahul.sharma@authnull.lab"
            style={{ height: 36 }}
          />
        </FilterField>

        <FilterField label="Status">
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: "100%", height: 36 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value ?? null)}
            options={STATUS_OPTIONS}
          />
        </FilterField>

        <FilterField label="Sort">
          <Select
            placeholder="Default"
            style={{ width: "100%", height: 36 }}
            value={sortBy}
            onChange={(value) => setSortBy(value ?? "default")}
            options={SORT_OPTIONS}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={sortedUsers}
          loading={isLoading}
          rowKey={rowKeyOf}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0 ? "No users match your filters." : "No users found."
          )}
          expandable={{
            // The expand caret opens the row's authenticator enrollment panel —
            // the same panel "Register device" targets.
            expandedRowKeys: enrollmentUserKey ? [enrollmentUserKey] : [],
            onExpand: (expanded, user) =>
              setEnrollmentUserKey(expanded ? rowKeyOf(user) : null),
            expandedRowRender: (user) => (
              <UserEnrollment
                userId={user.userId ?? user.id}
                email={user.emailId}
                // Resolves enrollmentApplies for this user's directory, so an
                // Okta/Duo org is not offered AuthNull invites. The directory
                // label resolves server-side alongside the AD FQDN.
                domain={user.activeDirectoryName}
                openSignal={
                  registerSignal.key === rowKeyOf(user) ? registerSignal.n : 0
                }
                onNotify={({ type, content }) => notification[type]?.({ content })}
              />
            ),
          }}
          pagination={{
            current: paginationParams.page,
            pageSize: PAGE_SIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setPaginationParams({ page, pageSize: PAGE_SIZE }),
          }}
        />
      </TableCard>

      {isAddUser && (
        <AddUser
          isAddUser={isAddUser}
          setIsAddUser={setIsAddUser}
          notification={notification}
          fetchUsers={fetchAdUser}
          run={isTour}
          setRun={setTour}
        />
      )}
      {isImportUser && (
        <ImportUsers
          isImportUser={isImportUser}
          setIsImportUser={setIsImportUser}
          notification={notification}
          fetchUsers={fetchAdUser}
          run={isTour}
          setRun={setTour}
        />
      )}
    </PageShell>
  );
}
